import {
  Input,
  Select,
  Brand,
  Button,
  Dropdown,
  EmptyState,
  ErrorNotice,
  IconButton,
  LoadingScreen,
  MenuAction,
  MenuDivider,
  Modal,
} from '@kiipu/ui';
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useRouterState, useRouter } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  Archive,
  ArrowClockwise,
  ArrowRight,
  BookOpen,
  CaretDown,
  CaretRight,
  Check,
  DotsThree,
  FolderSimple,
  GearSix,
  Hash,
  List,
  MagnifyingGlass,
  Plus,
  SidebarSimple,
  SlidersHorizontal,
  SquaresFour,
  Star,
  Tag as TagIcon,
  Trash,
  Tray,
  X,
  Columns,
  SignOut,
  ShieldCheck,
  Selection,
} from '@phosphor-icons/react';

import { api, type ItemPatch, type LibraryItem, platformName } from '../api';
import { useAppearance, useLibraryEvents, useLogout, useSession, useToast } from '../context';
import { AuthPage } from './auth';
import {
  CollectionLayout,
  CollectionSkeleton,
  ItemCard,
  ItemRow,
  type ItemActions,
  type ViewMode,
} from '../components/collection';
import { Reader } from '../components/reader';
import { GroupManager, OrganizeDialog } from '../components/groups';
import { SaveDialog } from '../components/save-dialog';
import type { LibrarySearch } from '../main';
import { rememberLibraryReturn, readLibraryReturn } from '../lib/settings-navigation';
import { DesktopAccountMenu } from '../components/desktop-account-menu';
import { desktopInteractionOwnsKeys } from '../lib/desktop-interaction';
import {
  DesktopConnectionStatus,
  DesktopNavigation,
  DesktopResizeHandle,
  desktopWorkspaceStyle,
  useDesktopLayout,
} from '../components/desktop-workspace';
import { resetCollectionPosition, useCollectionPosition } from '../hooks/collection-position';
import { CollectionFilters } from '../components/collection-filters';
import { useCollectionFilterPreferences } from '../lib/collection-filter-preferences';
import {
  conditionKeys,
  conditionsFromSearch,
  firstFilterValue,
  hasFilterSnapshot,
  libraryQueryParams,
  parseLibrarySearch,
  scopeForSearch,
  withConditions,
} from '../lib/collection-filter-routing';
import { collectionFilterScopeKey, type CollectionFilterConditions } from '@kiipu/app-protocol';
import { folderPath } from '../components/organization-helpers';
import {
  dispatchDesktopCommand,
  useDesktopCommands,
  workspaceCommands,
} from '../lib/desktop-commands';
import type { DesktopCommand } from '@kiipu/desktop-protocol';
import {
  canMutate,
  desktopBridge,
  isDesktop,
  openSettings,
  platformState,
  usePlatform,
  writeClipboardText,
} from '../lib/client-platform';

function readPreference(key: string, fallback: string) {
  try {
    return localStorage.getItem(key) || fallback;
  } catch {
    return fallback;
  }
}
const searchCommands = ['search', 'context-search'] as const;
const DesktopSearchWorkspace = lazy(() =>
  import('../components/desktop-search').then((module) => ({
    default: module.DesktopSearchWorkspace,
  })),
);
const DesktopCommandPalette = lazy(() =>
  import('../components/desktop-search').then((module) => ({
    default: module.DesktopCommandPalette,
  })),
);
const modeIcons = { list: List, card: SquaresFour, masonry: Columns };
const modeLabels = { list: '列表', card: '卡片', masonry: '瀑布流' };
function readCollapsedFolders(key: string): Set<string> {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(key) || '[]');
    return new Set(
      Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string') : [],
    );
  } catch {
    return new Set();
  }
}

export function Library() {
  const router = useRouter();
  const routeLocation = useRouterState({ select: (state) => state.location });
  // Match search can lag behind location during a replacement. Read the URL and
  // its history state from one snapshot so preference restoration cannot chase itself.
  const search = useMemo(() => parseLibrarySearch(routeLocation.search), [routeLocation.search]);
  const desktop = isDesktop();
  const pathname = routeLocation.pathname;
  const globalSearch = desktop && pathname === '/search';
  const navigate = useNavigate();
  const client = useQueryClient();
  const session = useSession();
  const user = session.data?.user;
  const origin = platformState()?.apiOrigin || window.location.origin;
  const filterPreferences = useCollectionFilterPreferences(user?.id || '', origin);
  const filterScope = pathname === '/' ? scopeForSearch(search) : null;
  const filterScopeKey = filterScope ? collectionFilterScopeKey(filterScope) : '';
  const conditions = useMemo(() => conditionsFromSearch(search), [search]);
  const conditionsSignature = JSON.stringify(conditions);
  const savedConditions = filterScope ? filterPreferences.current(filterScope) : {};
  const savedConditionsSignature = JSON.stringify(savedConditions);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const filterEditRevision = useRef<{ scope: string; revision: number } | null>(null);
  const [historySnapshotKey, setHistorySnapshotKey] = useState<string | null>(null);
  const routeKey = routeLocation.state.__TSR_key || routeLocation.href;
  const followsFilterPreference = Boolean(
    filterScope &&
      historySnapshotKey !== routeKey &&
      (routeLocation.state.kiipuFilterLive === true || !hasFilterSnapshot(search)),
  );
  const restoringFilters = Boolean(
    followsFilterPreference &&
      (!filterPreferences.ready ||
        (!filterMenuOpen &&
          (!hasFilterSnapshot(search) || conditionsSignature !== savedConditionsSignature))),
  );
  const initializedFilterScope = useRef('');
  const filterRestoreRequest = useRef('');
  useEffect(
    () =>
      router.history.subscribe(({ location, action }) => {
        if (!['PUSH', 'REPLACE'].includes(action.type))
          setHistorySnapshotKey(location.state.__TSR_key || location.href);
      }),
    [router],
  );
  useEffect(() => {
    setFilterMenuOpen(false);
    initializedFilterScope.current = '';
    filterRestoreRequest.current = '';
  }, [origin, user?.id, filterScopeKey]);
  useEffect(() => {
    if (!filterScope || !followsFilterPreference || !filterPreferences.ready || filterMenuOpen)
      return;
    const key = `${origin}:${user?.id}:${filterScopeKey}`;
    if (hasFilterSnapshot(search) && conditionsSignature === savedConditionsSignature) {
      initializedFilterScope.current = key;
      filterRestoreRequest.current = '';
      return;
    }
    // Query removal and router transitions can render before the replacement commits.
    // Apply each source/target snapshot once rather than scheduling the same navigation again.
    const requestKey = JSON.stringify([key, conditionsSignature, savedConditionsSignature]);
    if (filterRestoreRequest.current === requestKey) return;
    filterRestoreRequest.current = requestKey;
    const wasInitialized = initializedFilterScope.current === key;
    initializedFilterScope.current = key;
    if (wasInitialized && conditionsSignature !== savedConditionsSignature) {
      const nextFilters: Record<string, unknown> = { ...filters };
      for (const dimension of conditionKeys) delete nextFilters[dimension];
      Object.assign(nextFilters, savedConditions);
      client.removeQueries({ queryKey: ['items', user?.id, nextFilters], exact: true });
      resetCollectionPosition(positionKey(nextFilters));
    }
    void navigate({
      to: '/',
      search: withConditions(
        { ...search, ...(wasInitialized ? { item: undefined } : {}) },
        savedConditions,
      ),
      replace: true,
      state: (previous) => ({ ...previous, kiipuFilterLive: true }),
    });
  }, [
    origin,
    user?.id,
    filterScopeKey,
    followsFilterPreference,
    filterPreferences.ready,
    filterMenuOpen,
    conditionsSignature,
    savedConditionsSignature,
    navigate,
    search,
    routeKey,
  ]);
  const toast = useToast();
  const { t } = useTranslation();
  const logout = useLogout();
  useAppearance(Boolean(user));
  useLibraryEvents(user?.id);
  const [saveOpen, setSaveOpen] = useState(false);
  const [manager, setManager] = useState<'folders' | 'tags' | null>(null);
  const [organize, setOrganize] = useState<LibraryItem[] | null>(null);
  const [permanentDelete, setPermanentDelete] = useState<LibraryItem | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { layout, update: updateLayout } = useDesktopLayout(user?.id || '');
  const [sidebarOverlay, setSidebarOverlay] = useState(false);
  const [collectionSearchOpen, setCollectionSearchOpen] = useState(Boolean(search.q));
  const searchComposing = useRef(false);
  usePlatform();
  const [filtersOpen, setFiltersOpen] = useState(
    Boolean(search.platform || search.status || search.kind || search.quality),
  );
  const [searchText, setSearchText] = useState(search.q || '');
  const searchInput = useRef<HTMLInputElement>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const collectionScroll = useRef<HTMLDivElement>(null);
  const [bulkAction, setBulkAction] = useState<'refresh' | 'delete' | null>(null);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  const [bulkError, setBulkError] = useState('');
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const batchRequest = useRef<{ fingerprint: string; mutationId: string } | null>(null);
  const trashRequests = useRef(new Set<string>());
  const [focus, setFocus] = useState(false);
  const collectionHeader = useRef<HTMLElement>(null);
  const [headerWidth, setHeaderWidth] = useState(480);
  useEffect(() => {
    batchRequest.current = null;
  }, [selecting]);
  const [viewport, setViewport] = useState(window.innerWidth);
  const stageRef = useRef<HTMLElement>(null);
  const [stageWidth, setStageWidth] = useState(Math.max(0, window.innerWidth - 238));
  useEffect(() => {
    const element = stageRef.current;
    if (!element) return;
    const measure = () => setStageWidth(element.clientWidth);
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    measure();
    return () => observer.disconnect();
  }, [user?.id]);
  useEffect(() => {
    if (user?.id && pathname === '/') rememberLibraryReturn(user.id, search);
  }, [user?.id, search, pathname]);
  const [webListWidth, setWebListWidth] = useState(
    () => Number(readPreference('kiipu.list-width.v1', '480')) || 480,
  );
  const listWidth = desktop ? layout.listWidth : webListWidth;
  const setListWidth = (value: number) =>
    desktop ? updateLayout({ listWidth: value }) : setWebListWidth(value);
  const [mode, setMode] = useState<ViewMode>('list');
  const scope = `${search.view || 'all'}:${search.folderId || ''}:${search.tagId || ''}`;
  const prefKey = `kiipu.collection-mode.v1:${user?.id || ''}:${scope}`;
  useEffect(() => {
    const value = readPreference(prefKey, 'list');
    setMode(['list', 'card', 'masonry'].includes(value) ? (value as ViewMode) : 'list');
  }, [prefKey]);
  useEffect(() => {
    batchRequest.current = null;
    setSelected(new Set());
    setSelecting(false);
  }, [scope, search.q, search.status, search.platform, search.kind, search.quality]);
  useEffect(() => {
    const update = () => setViewport(window.innerWidth);
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  useEffect(() => {
    const element = collectionHeader.current;
    if (!element) return;
    const observer = new ResizeObserver(() => setHeaderWidth(element.clientWidth));
    observer.observe(element);
    setHeaderWidth(element.clientWidth);
    return () => observer.disconnect();
  }, [globalSearch, user?.id]);
  useEffect(() => {
    if (search.q) setCollectionSearchOpen(true);
  }, [search.q]);
  useLayoutEffect(() => {
    if (desktop && collectionSearchOpen && !globalSearch)
      searchInput.current?.focus({ preventScroll: true });
  }, [desktop, collectionSearchOpen, globalSearch]);
  useEffect(() => {
    setSidebarOverlay(false);
  }, [pathname, user?.id]);
  useEffect(() => {
    setSearchText(search.q || '');
  }, [search.q]);
  const updateSearch = useCallback(
    (patch: Partial<LibrarySearch>, replace = false) => {
      void navigate({
        to: '/',
        search: (previous) => {
          const next = { ...previous, ...patch };
          return Object.fromEntries(
            Object.entries(next).filter(([, value]) => value !== undefined && value !== ''),
          );
        },
        replace,
        state: (previous) => ({ ...previous, kiipuFilterLive: followsFilterPreference }),
      });
    },
    [navigate, followsFilterPreference],
  );
  const toggleSidebar = () => {
    const wasFocused = focus && Boolean(search.item);
    setFocus(false);
    if (wasFocused) {
      if (viewport <= 1060) setSidebarOverlay(true);
      else updateLayout({ sidebarVisible: true });
    } else if (viewport <= 1060) setSidebarOverlay((value) => !value);
    else updateLayout({ sidebarVisible: !layout.sidebarVisible });
  };
  const showGlobalSearch = () => {
    setSidebarOverlay(false);
    if (globalSearch) window.dispatchEvent(new Event('kiipu:global-search-focus'));
    else void navigate({ to: '/search', search: {} });
  };
  const showCollectionSearch = (text?: string) => {
    if (globalSearch) {
      void navigate({
        to: '/',
        search: {
          ...readLibraryReturn(user?.id),
          ...(text === undefined ? {} : { q: text }),
          desktopAction: 'context-search',
        },
      });
    } else if (text !== undefined) {
      setSearchText(text);
      updateSearch({ q: text || undefined });
    }
    setCollectionSearchOpen(true);
    requestAnimationFrame(() => searchInput.current?.focus({ preventScroll: true }));
  };
  useDesktopCommands(
    searchCommands,
    (command) => {
      if (desktopInteractionOwnsKeys()) return;
      if (command === 'search') showGlobalSearch();
      else if (document.activeElement?.closest('.reader-pane') || (globalSearch && search.item))
        dispatchDesktopCommand('find-in-content');
      else if (globalSearch) window.dispatchEvent(new Event('kiipu:global-search-focus'));
      else showCollectionSearch();
    },
    Boolean(user),
  );
  const foldersQuery = useQuery({
    queryKey: ['folders'],
    queryFn: api.folders,
    enabled: Boolean(user),
  });
  const tagsQuery = useQuery({
    queryKey: ['tags'],
    queryFn: api.tags,
    enabled: Boolean(user),
  });
  const usageQuery = useQuery({
    queryKey: ['usage'],
    queryFn: api.usage,
    enabled: Boolean(user),
  });
  const folders = foldersQuery.data?.items || [];
  const tags = tagsQuery.data?.items || [];
  const folderExpansionKey = `kiipu.folder-collapse.v1:${user?.id || ''}`;
  const expandedForFolder = useRef('');
  const [folderCollapse, setFolderCollapse] = useState(() => ({
    key: folderExpansionKey,
    ids: readCollapsedFolders(folderExpansionKey),
  }));
  useEffect(() => {
    setFolderCollapse((value) =>
      value.key === folderExpansionKey
        ? value
        : { key: folderExpansionKey, ids: readCollapsedFolders(folderExpansionKey) },
    );
  }, [folderExpansionKey]);
  useEffect(() => {
    if (!search.folderId) {
      expandedForFolder.current = '';
      return;
    }
    if (!folders.some((folder) => folder.id === search.folderId)) return;
    const ancestors = new Set<string>();
    let id = folders.find((folder) => folder.id === search.folderId)?.parentId;
    while (id && !ancestors.has(id)) {
      ancestors.add(id);
      id = folders.find((folder) => folder.id === id)?.parentId;
    }
    const selectionKey = `${folderExpansionKey}:${search.folderId}:${[...ancestors].join('/')}`;
    if (expandedForFolder.current === selectionKey) return;
    expandedForFolder.current = selectionKey;
    setFolderCollapse((value) => {
      if (value.key !== folderExpansionKey || ![...ancestors].some((id) => value.ids.has(id)))
        return value;
      const ids = new Set([...value.ids].filter((id) => !ancestors.has(id)));
      try {
        localStorage.setItem(folderExpansionKey, JSON.stringify([...ids]));
      } catch {}
      return { key: folderExpansionKey, ids };
    });
  }, [search.folderId, folders, folderExpansionKey]);
  const collapsedFolders =
    folderCollapse.key === folderExpansionKey ? folderCollapse.ids : new Set<string>();
  function toggleFolder(id: string) {
    setFolderCollapse((value) => {
      const ids = new Set(
        value.key === folderExpansionKey ? value.ids : readCollapsedFolders(folderExpansionKey),
      );
      if (ids.has(id)) ids.delete(id);
      else ids.add(id);
      try {
        localStorage.setItem(folderExpansionKey, JSON.stringify([...ids]));
      } catch {}
      return { key: folderExpansionKey, ids };
    });
  }
  const folderRows: { folder: (typeof folders)[number]; depth: number }[] = [];
  const walkFolders = (parentId: string | null, depth = 0) => {
    if (depth >= 5) return;
    for (const folder of folders.filter((entry) => entry.parentId === parentId)) {
      folderRows.push({ folder, depth });
      if (!collapsedFolders.has(folder.id)) walkFolders(folder.id, depth + 1);
    }
  };
  walkFolders(null);
  const filters = {
    folderId: search.view === 'unfiled' ? 'unfiled' : search.folderId,
    tagId: search.tagId,
    favorite: search.view === 'favorites' ? 'true' : undefined,
    trash: search.view === 'trash' ? 'true' : undefined,
    q: search.q,
    ...(filterScope
      ? conditions
      : {
          status: search.status,
          platform: search.platform,
          kind: search.kind,
          quality: search.quality,
        }),
  };
  const query = useInfiniteQuery({
    queryKey: ['items', user?.id, filters],
    initialPageParam: '',
    gcTime: 10 * 60_000,
    queryFn: ({ pageParam, signal }) => {
      const params = libraryQueryParams(filters);
      if (pageParam) params.set('cursor', pageParam);
      return api.items(params, signal);
    },
    getNextPageParam: (page) => page.nextCursor || undefined,
    enabled: Boolean(user) && pathname === '/' && !restoringFilters,
    refetchInterval: (query) =>
      query.state.data?.pages.some((page) =>
        page.items.some((item) => item.status === 'queued' || item.status === 'running'),
      )
        ? 4000
        : false,
  });
  const items = restoringFilters ? [] : query.data?.pages.flatMap((page) => page.items) || [];
  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['items'] });
    void client.invalidateQueries({ queryKey: ['item'] });
    void client.invalidateQueries({ queryKey: ['usage'] });
  };
  const patchMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: ItemPatch }) =>
      api.patch(id, { ...patch, mutationId: patch.mutationId || crypto.randomUUID() }),
    onSuccess: (_result, { id, patch }) => {
      refresh();
      if (patch.trashed === false) toast('内容已恢复');
      if (patch.trashed === true) {
        const mutationId = crypto.randomUUID();
        toast('内容已移入回收站', false, {
          duration: 8000,
          action: {
            label: '撤销',
            run: async () => {
              await api.patch(id, { trashed: false, mutationId });
              refresh();
            },
          },
        });
      }
    },
    onError: (error) => toast(error.message, true),
    onSettled: (_data, _error, { id, patch }) => {
      if (patch.trashed !== undefined) trashRequests.current.delete(id);
    },
  });
  const batch = useMutation({
    mutationFn: (patch: ItemPatch) => {
      const ids = [...selected];
      const fingerprint = JSON.stringify({ ids, patch });
      if (batchRequest.current?.fingerprint !== fingerprint)
        batchRequest.current = { fingerprint, mutationId: crypto.randomUUID() };
      return api.batch(ids, patch, { mutationId: batchRequest.current.mutationId });
    },
    onSuccess: () => {
      batchRequest.current = null;
      refresh();
      setSelected(new Set());
      setSelecting(false);
      toast('所选内容已更新');
    },
    onError: (error) => toast(error.message, true),
  });
  const refreshMutation = useMutation({
    mutationFn: api.refresh,
    onSuccess: () => {
      refresh();
      toast(
        desktop
          ? '已加入本地重新读取，现有快照会继续保留。'
          : '已开始重新获取，现有快照会继续保留。',
      );
    },
    onError: (error) => toast(error.message, true),
  });
  const deleteMutation = useMutation({
    mutationFn: api.remove,
    onSuccess: () => {
      refresh();
      if (search.item === permanentDelete?.id) updateSearch({ item: undefined });
      setPermanentDelete(null);
      toast('内容已永久删除');
    },
  });
  const actions: ItemActions = {
    open: (id) => updateSearch({ item: id }),
    favorite: (item) => {
      if (!patchMutation.isPending)
        patchMutation.mutate({
          id: item.id,
          patch: { favorite: !item.favorite },
        });
    },
    trash: (item) => {
      if (trashRequests.current.has(item.id)) return;
      trashRequests.current.add(item.id);
      patchMutation.mutate({ id: item.id, patch: { trashed: !item.deletedAt } });
    },
    organize: (item) => setOrganize([item]),
    refresh: (item) => refreshMutation.mutate(item.id),
    remove: setPermanentDelete,
    copy: (item) => {
      void writeClipboardText(item.url).then(
        () => toast('链接已复制'),
        () => toast('无法访问剪贴板，请在阅读器中打开原文后复制。', true),
      );
    },
  };
  const activeIndex = items.findIndex((item) => item.id === search.item);
  const next = activeIndex >= 0 ? items[activeIndex + 1]?.id : undefined;
  const previous = activeIndex > 0 ? items[activeIndex - 1]?.id : undefined;
  useDesktopCommands(
    workspaceCommands,
    (command) => {
      if (command === 'save') {
        if (canMutate()) setSaveOpen(true);
        else toast('请联网后保存链接。', true);
      }
      if (command === 'refresh') refresh();
      if (command === 'toggle-sidebar') toggleSidebar();
      if (command === 'previous' && previous && !globalSearch) updateSearch({ item: previous });
      if (command === 'next' && next && !globalSearch) updateSearch({ item: next });
    },
    Boolean(user),
  );
  useEffect(() => {
    if (
      search.desktopAction &&
      [...workspaceCommands, ...searchCommands].includes(search.desktopAction as DesktopCommand)
    ) {
      dispatchDesktopCommand(search.desktopAction as DesktopCommand);
      updateSearch({ desktopAction: undefined }, true);
    }
  }, [search.desktopAction, updateSearch]);
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (
        event.defaultPrevented ||
        event.isComposing ||
        (desktop
          ? desktopInteractionOwnsKeys()
          : document.querySelector('[role="dialog"], [role="menu"]'))
      )
        return;
      const target = event.target;
      const editing =
        target instanceof HTMLElement &&
        (target.matches('input,textarea,select') || target.isContentEditable);
      if (
        desktop &&
        (event.metaKey || event.ctrlKey) &&
        !event.altKey &&
        ['f', 'k'].includes(event.key.toLowerCase())
      ) {
        event.preventDefault();
        if (event.key.toLowerCase() === 'k') dispatchDesktopCommand('command-palette');
        else if (event.shiftKey) showGlobalSearch();
        else if (
          (target instanceof Element && target.closest('.reader-pane')) ||
          (globalSearch && search.item)
        )
          dispatchDesktopCommand('find-in-content');
        else if (globalSearch) window.dispatchEvent(new Event('kiipu:global-search-focus'));
        else showCollectionSearch();
        return;
      }
      if (!desktop && (event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        updateSearch({ item: undefined });
        requestAnimationFrame(() => searchInput.current?.focus());
        return;
      }
      if (editing || event.metaKey || event.ctrlKey || event.altKey || globalSearch) return;
      if (event.key === 'n') {
        event.preventDefault();
        setSaveOpen(true);
      }
      if (event.key === 'Escape') {
        if (focus) setFocus(false);
        else updateSearch({ item: undefined });
      }
      if (event.key === 'j' && next) updateSearch({ item: next });
      if (event.key === 'k' && previous) updateSearch({ item: previous });
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
      clearTimeout(searchTimer.current);
    };
  }, [focus, next, previous, updateSearch, desktop, globalSearch, viewport, layout.sidebarVisible]);
  useEffect(() => {
    if (!sidebarOpen && !sidebarOverlay) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    const sidebar = document.querySelector<HTMLElement>('.sidebar');
    const getControls = () =>
      [
        ...(sidebar?.querySelectorAll<HTMLElement>('button, a[href], input, [tabindex="0"]') || []),
      ].filter((entry) => entry.getClientRects().length && !entry.hasAttribute('disabled'));
    getControls()[0]?.focus();
    const trap = (event: KeyboardEvent) => {
      const nested = [
        ...document.querySelectorAll<HTMLElement>(
          '[role="menu"], [role="listbox"], [role="dialog"], [role="alertdialog"]',
        ),
      ].some(
        (element) =>
          element !== sidebar &&
          !sidebar?.contains(element) &&
          element.getClientRects().length > 0 &&
          !element.closest('[data-ending-style]'),
      );
      if (nested) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        setSidebarOpen(false);
        setSidebarOverlay(false);
      }
      if (event.key !== 'Tab') return;
      const controls = getControls();
      const first = controls[0],
        last = controls.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    window.addEventListener('keydown', trap, true);
    return () => {
      window.removeEventListener('keydown', trap, true);
      previousFocus?.focus();
    };
  }, [sidebarOpen, sidebarOverlay]);
  function changeMode(value: ViewMode) {
    setMode(value);
    try {
      localStorage.setItem(prefKey, value);
    } catch {}
  }
  function check(id: string) {
    if (!selected.has(id) && selected.size >= 100) {
      toast('一次最多选择 100 条内容。', true);
      return;
    }
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function go(patch: LibrarySearch, snapshot = false) {
    setSidebarOpen(false);
    setSidebarOverlay(false);
    setFocus(false);
    clearTimeout(searchTimer.current);
    setHistorySnapshotKey(null);
    const target = scopeForSearch(patch);
    const live = Boolean(target && !snapshot);
    if (live) void filterPreferences.refresh();
    void navigate({
      to: '/',
      search:
        live && filterPreferences.ready
          ? withConditions(patch, filterPreferences.current(target!))
          : patch,
      state: (previous) => ({ ...previous, kiipuFilterLive: live }),
    });
  }
  function positionKey(value: Record<string, unknown>) {
    return `kiipu.collection-position.v2:${JSON.stringify([origin, user?.id || '', libraryQueryParams(value).toString(), mode])}`;
  }
  function filterOpenChanged(open: boolean) {
    setFilterMenuOpen(open);
    filterEditRevision.current =
      open && filterScope
        ? {
            scope: `${origin}:${user?.id}:${filterScopeKey}`,
            revision: filterPreferences.preference(filterScope).revision,
          }
        : null;
  }
  function changeCollectionConditions(next: CollectionFilterConditions) {
    if (!filterScope) return;
    const nextFilters: Record<string, unknown> = { ...filters };
    for (const key of conditionKeys) delete nextFilters[key];
    Object.assign(nextFilters, next);
    client.removeQueries({ queryKey: ['items', user?.id, nextFilters], exact: true });
    resetCollectionPosition(positionKey(nextFilters));
    setSelected(new Set());
    setSelecting(false);
    setHistorySnapshotKey(null);
    const editing = filterEditRevision.current;
    filterPreferences.stage(
      filterScope,
      next,
      editing?.scope === `${origin}:${user?.id}:${filterScopeKey}` ? editing.revision : undefined,
    );
    void navigate({
      to: '/',
      search: withConditions({ ...search, item: undefined }, next),
      replace: true,
      state: (previous) => ({ ...previous, kiipuFilterLive: true }),
    });
  }
  function resolveFilterConflict(useCloud: boolean) {
    if (!filterScope) return;
    if (useCloud) filterPreferences.adopt(filterScope);
    else {
      filterPreferences.stage(filterScope, conditions);
      void filterPreferences.overwrite(filterScope);
    }
    setHistorySnapshotKey(null);
    void navigate({
      to: '/',
      search: withConditions(
        { ...search, item: undefined },
        filterPreferences.current(filterScope),
      ),
      replace: true,
      state: (previous) => ({ ...previous, kiipuFilterLive: true }),
    });
  }
  const label = search.folderId
    ? folders.find((folder) => folder.id === search.folderId)?.name || '文件夹'
    : search.tagId
      ? tags.find((tag) => tag.id === search.tagId)?.name || '标签'
      : { favorites: '收藏', unfiled: '未整理', trash: '回收站' }[search.view || ''] || '全部内容';
  const selectedFolder = folders.find((folder) => folder.id === search.folderId);
  const scopeLabel = selectedFolder ? folderPath(selectedFolder, folders) : label;
  const scopedSearch = Boolean(
    search.folderId || search.tagId || (search.view && search.view !== 'all'),
  );
  const filterSaveStatus = filterScope ? filterPreferences.status(filterScope) : null;
  const legacyConditions = {
    platform: firstFilterValue(search.platform),
    status: firstFilterValue(search.status),
    kind: firstFilterValue(search.kind),
    quality: firstFilterValue(search.quality),
  };
  const activeFilters = [
    {
      key: 'platform' as const,
      value: legacyConditions.platform,
      label: legacyConditions.platform ? platformName(legacyConditions.platform) : '',
    },
    {
      key: 'status' as const,
      value: legacyConditions.status,
      label:
        (
          {
            completed: '已完成',
            queued: '等待获取',
            running: '正在获取',
            failed: '获取未完成',
          } as Record<string, string>
        )[legacyConditions.status || ''] || legacyConditions.status,
    },
    {
      key: 'kind' as const,
      value: legacyConditions.kind,
      label:
        ({ article: '文章', post: '笔记', video: '视频' } as Record<string, string>)[
          legacyConditions.kind || ''
        ] || legacyConditions.kind,
    },
    {
      key: 'quality' as const,
      value: legacyConditions.quality,
      label:
        (
          { complete: '完整保存', partial: '部分保存', link_only: '仅保存链接' } as Record<
            string,
            string
          >
        )[legacyConditions.quality || ''] || legacyConditions.quality,
    },
  ].filter((filter) => filter.value);
  function clearFilters() {
    if (filterScope) {
      changeCollectionConditions({});
      return;
    }
    updateSearch({
      platform: undefined,
      status: undefined,
      kind: undefined,
      quality: undefined,
      item: undefined,
    });
  }
  function searchAll() {
    go(
      withConditions(
        {
          view: 'all',
          q: searchText.trim() || undefined,
          platform: search.platform,
          status: search.status,
          kind: search.kind,
          quality: search.quality,
        },
        conditions,
      ),
      true,
    );
  }
  const split = viewport >= 1024 && stageWidth >= 681;
  const boundedWidth = Math.min(Math.max(300, listWidth), Math.max(300, stageWidth - 381));
  useCollectionPosition({
    key: positionKey(filters),
    ref: collectionScroll,
    pages: query.data?.pages.length || 0,
    ready: Boolean(user) && !restoringFilters && !query.isPending,
    hidden: Boolean(search.item) && (!split || focus),
    hasNextPage: Boolean(query.hasNextPage),
    fetching: query.isFetching,
    fetchNextPage: query.fetchNextPage,
  });
  async function runBulkAction() {
    if (!bulkAction || bulkProgress) return;
    const ids = [...selected];
    const failed: string[] = [];
    const messages = new Set<string>();
    setBulkError('');
    setBulkProgress({ done: 0, total: ids.length });
    for (const [index, id] of ids.entries()) {
      try {
        if (bulkAction === 'delete') await api.remove(id);
        else await api.refresh(id);
      } catch (error) {
        failed.push(id);
        messages.add(error instanceof Error ? error.message : '请求未完成');
      }
      setBulkProgress({ done: index + 1, total: ids.length });
    }
    refresh();
    setBulkProgress(null);
    setSelected(new Set(failed));
    if (
      search.item &&
      ids.includes(search.item) &&
      !failed.includes(search.item) &&
      bulkAction === 'delete'
    )
      updateSearch({ item: undefined });
    if (failed.length)
      setBulkError(
        `已完成 ${ids.length - failed.length} 条，${failed.length} 条未完成。${[...messages].join('；')}。失败内容仍保留选择，可重试。`,
      );
    else {
      setBulkAction(null);
      setSelecting(false);
      toast(
        bulkAction === 'delete'
          ? `已永久删除 ${ids.length} 条内容`
          : `已受理 ${ids.length} 条重新获取任务`,
      );
    }
  }
  const resizerCleanup = useRef<(() => void) | null>(null);
  useEffect(() => () => resizerCleanup.current?.(), []);
  if (session.isPending) return <LoadingScreen />;
  if (session.isError)
    return (
      <main className="service-error">
        <Brand />
        <ErrorNotice
          error={session.error}
          retry={() => {
            void session.refetch();
          }}
        />
        <p className="muted">暂时无法连接 Kiipu 服务，你的内容没有被删除。</p>
      </main>
    );
  if (!user) return <AuthPage embedded />;
  const sidebarDocked =
    desktop && viewport > 1060 && layout.sidebarVisible && !(focus && search.item);
  const sidebarExpanded = sidebarDocked || sidebarOverlay;
  return (
    <div
      className={`workspace${desktop ? ' desktop-workspace' : ''}`}
      data-global-search={globalSearch || undefined}
      data-sidebar-overlay={sidebarOverlay || undefined}
      data-desktop-nav-in-header={desktop && !sidebarDocked}
      data-desktop-platform={/Mac|iPhone|iPad/.test(navigator.platform) ? 'mac' : 'other'}
      data-reading={Boolean(search.item)}
      data-desktop-sidebar-hidden={desktop && !sidebarExpanded}
      data-split={split}
      data-focus={focus && Boolean(search.item)}
      style={
        desktop
          ? desktopWorkspaceStyle(layout.sidebarWidth, boundedWidth)
          : ({ '--list-width': `${boundedWidth}px` } as CSSProperties)
      }
    >
      <aside
        id="workspace-navigation"
        className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}
        aria-label="主导航"
        role={sidebarOpen || sidebarOverlay ? 'dialog' : undefined}
        aria-modal={sidebarOpen || sidebarOverlay || undefined}
      >
        {desktop ? <div className="desktop-sidebar-titlebar" /> : null}
        <div className="sidebar-top">
          <Link to="/" search={{}} aria-label="Kiipu 首页">
            <Brand />
          </Link>
          {desktop ? (
            <IconButton
              label="保存链接（⌘N）"
              disabled={!canMutate()}
              onClick={() => {
                setSidebarOverlay(false);
                setSaveOpen(true);
              }}
            >
              <Plus size={17} />
            </IconButton>
          ) : null}
          <button
            className="sidebar-search"
            aria-label="搜索内容"
            onClick={() => {
              setSidebarOpen(false);
              if (desktop) {
                showGlobalSearch();
                return;
              }
              updateSearch({ item: undefined });
              requestAnimationFrame(() => searchInput.current?.focus());
            }}
          >
            <MagnifyingGlass size={16} />
            <span>{t('搜索内容')}</span>
            <kbd>⌘ K</kbd>
          </button>
          <IconButton
            label="关闭侧栏"
            className="mobile-sidebar-close"
            onClick={() => setSidebarOpen(false)}
          >
            <X size={18} />
          </IconButton>
        </div>
        {!desktop ? (
          <div className="sidebar-add">
            <Button
              variant="primary"
              disabled={!canMutate()}
              onClick={() => {
                setSidebarOpen(false);
                setSaveOpen(true);
              }}
            >
              <Plus size={17} />
              {t('保存链接')}
              <span className="shortcut">N</span>
            </Button>
          </div>
        ) : null}
        <nav className="sidebar-nav">
          {(
            [
              { view: 'all', name: '全部内容', icon: SquaresFour },
              { view: 'favorites', name: '收藏', icon: Star },
              { view: 'unfiled', name: '未整理', icon: Tray },
            ] as const
          ).map(({ view, name, icon: Icon }) => (
            <button
              key={view}
              className="nav-item"
              aria-current={
                !globalSearch &&
                !search.folderId &&
                !search.tagId &&
                (search.view || 'all') === view
                  ? 'page'
                  : undefined
              }
              onClick={() => go({ view })}
            >
              <Icon size={18} />
              <span>{t(name)}</span>
            </button>
          ))}
          <div className="sidebar-section-heading">
            <span>{t('文件夹')}</span>
            <IconButton
              disabled={!canMutate()}
              label="管理文件夹"
              onClick={() => setManager('folders')}
            >
              <Plus size={14} />
            </IconButton>
          </div>
          {foldersQuery.isError ? (
            <button
              className="sidebar-error"
              onClick={() => {
                void foldersQuery.refetch();
              }}
            >
              文件夹加载失败，重试
            </button>
          ) : (
            folderRows.map(({ folder, depth }) => (
              <div key={folder.id} className="folder-nav-row" style={{ paddingLeft: depth * 12 }}>
                {folders.some((entry) => entry.parentId === folder.id) ? (
                  <button
                    className="folder-disclosure"
                    aria-label={`${collapsedFolders.has(folder.id) ? '展开' : '折叠'}文件夹 ${folder.name}`}
                    aria-expanded={!collapsedFolders.has(folder.id)}
                    onClick={() => toggleFolder(folder.id)}
                  >
                    {collapsedFolders.has(folder.id) ? (
                      <CaretRight size={12} />
                    ) : (
                      <CaretDown size={12} />
                    )}
                  </button>
                ) : (
                  <span className="folder-disclosure-spacer" />
                )}
                <button
                  className="nav-item"
                  aria-current={search.folderId === folder.id ? 'page' : undefined}
                  onClick={() => go({ folderId: folder.id })}
                >
                  <FolderSimple size={18} />
                  <span>{folder.name}</span>
                </button>
              </div>
            ))
          )}
          {!folders.length && !foldersQuery.isError ? (
            <button className="sidebar-empty" onClick={() => setManager('folders')}>
              创建一个文件夹
            </button>
          ) : null}
          <div className="sidebar-section-heading">
            <span>{t('标签')}</span>
            <IconButton label="管理标签" onClick={() => setManager('tags')}>
              <Plus size={14} />
            </IconButton>
          </div>
          {tagsQuery.isError ? (
            <button
              className="sidebar-error"
              onClick={() => {
                void tagsQuery.refetch();
              }}
            >
              标签加载失败，重试
            </button>
          ) : (
            tags.map((tag) => (
              <button
                key={tag.id}
                className="nav-item"
                aria-current={search.tagId === tag.id ? 'page' : undefined}
                onClick={() => go({ tagId: tag.id })}
              >
                <Hash size={17} />
                <span>{tag.name}</span>
              </button>
            ))
          )}
          {!tags.length && !tagsQuery.isError ? (
            <button className="sidebar-empty" onClick={() => setManager('tags')}>
              用标签连接不同的内容
            </button>
          ) : null}
          <button
            className="nav-item"
            aria-current={search.view === 'trash' ? 'page' : undefined}
            onClick={() => go({ view: 'trash' })}
          >
            <Trash size={17} />
            <span>{t('回收站')}</span>
          </button>
        </nav>
        <div className="sidebar-bottom">
          {desktop ? (
            <>
              <DesktopConnectionStatus />
              <DesktopAccountMenu user={user} onLogout={() => logout.mutate()} />
            </>
          ) : (
            <>
              <Link className="nav-item" to="/settings">
                <GearSix size={15} />
                <span>设置</span>
              </Link>
              <div className="account-row">
                <div className="avatar">{(user.name || user.email).slice(0, 1).toUpperCase()}</div>
                <div className="account-copy">
                  <strong>{user.name || user.email.split('@')[0]}</strong>
                  <span>个人内容库</span>
                </div>
                <Dropdown label="账户菜单" icon={<DotsThree size={20} />}>
                  <MenuAction
                    onClick={() => {
                      if (isDesktop()) void openSettings();
                      else void navigate({ to: '/settings' });
                    }}
                  >
                    <GearSix size={16} />
                    设置
                  </MenuAction>
                  {user.role === 'admin' ? (
                    <MenuAction
                      onClick={() => {
                        if (isDesktop())
                          void desktopBridge()?.openExternal(
                            new URL('/admin', platformState()!.apiOrigin).href,
                          );
                        else void navigate({ to: '/admin' });
                      }}
                    >
                      <ShieldCheck size={16} />
                      管理后台
                    </MenuAction>
                  ) : null}
                  <MenuDivider />
                  <MenuAction onClick={() => logout.mutate()}>
                    <SignOut size={16} />
                    退出登录
                  </MenuAction>
                </Dropdown>
              </div>
            </>
          )}
        </div>
      </aside>
      {sidebarDocked ? (
        <DesktopResizeHandle
          kind="sidebar"
          value={layout.sidebarWidth}
          maximum={300}
          onChange={(sidebarWidth) => updateLayout({ sidebarWidth })}
        />
      ) : null}
      {sidebarOpen || sidebarOverlay ? (
        <button
          className="sidebar-scrim"
          aria-label="关闭侧栏"
          onClick={() => {
            setSidebarOpen(false);
            setSidebarOverlay(false);
          }}
        />
      ) : null}
      <main
        ref={stageRef}
        className="workspace-stage"
        inert={sidebarOverlay || (sidebarOpen && viewport < 768)}
      >
        {desktop && !sidebarDocked ? <DesktopConnectionStatus compact /> : null}
        {globalSearch ? (
          <Suspense fallback={<LoadingScreen />}>
            <DesktopSearchWorkspace actions={actions} folders={folders} tags={tags} />
          </Suspense>
        ) : (
          <>
            <CollectionFilters
              key={`${origin}:${user.id}:${scope}`}
              conditions={conditions}
              onChange={changeCollectionConditions}
              onOpenChange={filterOpenChanged}
            >
              {({ trigger, chips }) => (
                <section className="collection-pane" aria-label={label}>
                  <header ref={collectionHeader} className="collection-header">
                    <div className="header-title">
                      <IconButton
                        className="mobile-menu"
                        label="打开侧栏"
                        onClick={() => setSidebarOpen(true)}
                      >
                        <SidebarSimple size={19} />
                      </IconButton>
                      <h1>{t(label)}</h1>
                      <span className="collection-count">
                        {items.length ? `${items.length}${query.hasNextPage ? '+' : ''}` : ''}
                      </span>
                    </div>
                    {desktop && collectionSearchOpen ? (
                      <div
                        className="desktop-collection-search"
                        role="search"
                        onBlur={(event) => {
                          if (
                            !event.currentTarget.contains(event.relatedTarget) &&
                            !searchText.trim()
                          )
                            setCollectionSearchOpen(false);
                        }}
                      >
                        <MagnifyingGlass size={14} />
                        <Input
                          ref={searchInput}
                          type="search"
                          aria-label="搜索当前列表"
                          placeholder="搜索当前列表…"
                          maxLength={300}
                          value={searchText}
                          onCompositionStart={() => {
                            searchComposing.current = true;
                            clearTimeout(searchTimer.current);
                          }}
                          onCompositionEnd={(event) => {
                            searchComposing.current = false;
                            const value = event.currentTarget.value;
                            clearTimeout(searchTimer.current);
                            updateSearch({ q: value.trim() || undefined });
                          }}
                          onChange={(event) => {
                            const value = event.target.value;
                            setSearchText(value);
                            clearTimeout(searchTimer.current);
                            if (!searchComposing.current)
                              searchTimer.current = setTimeout(
                                () => updateSearch({ q: value.trim() || undefined }),
                                250,
                              );
                          }}
                          onKeyDown={(event) => {
                            if (event.nativeEvent.isComposing) return;
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              clearTimeout(searchTimer.current);
                              updateSearch({ q: searchText.trim() || undefined });
                            }
                            if (event.key === 'Escape') {
                              event.preventDefault();
                              event.stopPropagation();
                              clearTimeout(searchTimer.current);
                              setSearchText('');
                              updateSearch({ q: undefined });
                              setCollectionSearchOpen(false);
                              collectionHeader.current
                                ?.querySelector<HTMLElement>('button')
                                ?.focus();
                            }
                          }}
                        />
                        {searchText ? (
                          <IconButton
                            label="清空搜索"
                            onClick={() => {
                              clearTimeout(searchTimer.current);
                              setSearchText('');
                              updateSearch({ q: undefined });
                              setCollectionSearchOpen(false);
                            }}
                          >
                            <X size={13} />
                          </IconButton>
                        ) : null}
                      </div>
                    ) : null}
                    <div className="header-actions">
                      <div className="view-segments" aria-label="显示方式">
                        {(['list', 'card', 'masonry'] as const).map((value) => {
                          const Icon = modeIcons[value];
                          return (
                            <IconButton
                              key={value}
                              label={t(modeLabels[value])}
                              aria-pressed={mode === value}
                              onClick={() => changeMode(value)}
                            >
                              <Icon size={16} />
                            </IconButton>
                          );
                        })}
                      </div>
                      {filterScope ? (
                        trigger
                      ) : (
                        <IconButton
                          label="筛选内容"
                          aria-pressed={filtersOpen}
                          onClick={() => setFiltersOpen((value) => !value)}
                        >
                          <SlidersHorizontal size={17} />
                        </IconButton>
                      )}
                      <Dropdown label="列表操作" icon={<DotsThree size={19} />}>
                        {desktop && headerWidth < 840 ? (
                          <>
                            {(['list', 'card', 'masonry'] as const).map((value) => {
                              const Icon = modeIcons[value];
                              return (
                                <MenuAction key={value} onClick={() => changeMode(value)}>
                                  <Icon size={16} />
                                  {modeLabels[value]}
                                  {mode === value ? <Check size={14} /> : null}
                                </MenuAction>
                              );
                            })}
                            <MenuDivider />
                          </>
                        ) : null}
                        {desktop ? (
                          <MenuAction onClick={() => showCollectionSearch()}>
                            <MagnifyingGlass size={16} />
                            搜索当前列表<span className="menu-shortcut">⌘F</span>
                          </MenuAction>
                        ) : null}
                        <MenuAction
                          onClick={() => {
                            setSelecting((value) => !value);
                            setSelected(new Set());
                          }}
                        >
                          <Selection size={16} />
                          {selecting ? '取消选择' : '批量选择'}
                        </MenuAction>
                        <MenuAction
                          onClick={() => {
                            void query.refetch();
                          }}
                        >
                          <ArrowClockwise size={16} />
                          刷新内容
                        </MenuAction>
                        <MenuAction onClick={() => setSaveOpen(true)}>
                          <Plus size={16} />
                          保存链接
                        </MenuAction>
                      </Dropdown>
                    </div>
                  </header>
                  {(!desktop || collectionSearchOpen) && !desktop ? (
                    <div className="collection-search">
                      <MagnifyingGlass size={16} />
                      <Input
                        ref={searchInput}
                        type="search"
                        value={searchText}
                        aria-label="搜索标题、正文与备注"
                        placeholder="搜索标题、正文与备注…"
                        onChange={(event) => {
                          const value = event.target.value;
                          setSearchText(value);
                          clearTimeout(searchTimer.current);
                          searchTimer.current = setTimeout(
                            () => updateSearch({ q: value.trim() || undefined }, true),
                            300,
                          );
                        }}
                      />
                      {searchText ? (
                        <IconButton
                          label="清空搜索"
                          onClick={() => {
                            clearTimeout(searchTimer.current);
                            setSearchText('');
                            updateSearch({ q: undefined }, true);
                          }}
                        >
                          <X size={14} />
                        </IconButton>
                      ) : (
                        <kbd>⌘ K</kbd>
                      )}
                    </div>
                  ) : null}
                  {search.q || scopedSearch ? (
                    <div className="search-scope" aria-label="搜索范围">
                      <span title={scopeLabel}>在：{scopeLabel}</span>
                      {scopedSearch ? (
                        <button type="button" onClick={searchAll}>
                          搜索全部内容 <ArrowRight size={12} aria-hidden="true" />
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                  {filterScope ? (
                    chips
                  ) : activeFilters.length ? (
                    <div className="active-filter-list" aria-label="当前筛选条件">
                      {activeFilters.map((filter) => (
                        <button
                          key={filter.key}
                          type="button"
                          className="active-filter"
                          aria-label={`移除筛选 ${filter.label}`}
                          onClick={() => updateSearch({ [filter.key]: undefined, item: undefined })}
                        >
                          {filter.label}
                          <X size={12} aria-hidden="true" />
                        </button>
                      ))}
                    </div>
                  ) : null}
                  {filterScope &&
                  (filterSaveStatus?.offline ||
                    filterSaveStatus?.error ||
                    filterSaveStatus?.storageError ||
                    filterPreferences.error) ? (
                    <div className="collection-filter-feedback" role="status">
                      <span>
                        {filterSaveStatus?.storageError
                          ? '本机存储不可用，筛选暂时仅保留在当前窗口。'
                          : filterSaveStatus?.conflict
                            ? '此列表的筛选已在其他设备更新。'
                            : filterSaveStatus?.offline
                              ? '已在本机保存，联网后同步。'
                              : filterSaveStatus?.error
                                ? filterSaveStatus.deleted
                                  ? '此文件夹已删除，筛选不再同步。'
                                  : '当前筛选尚未同步，更改已保留。'
                                : '暂时无法读取云端筛选，更改不会被自动覆盖。'}
                      </span>
                      {filterSaveStatus?.conflict ? (
                        <>
                          <button type="button" onClick={() => resolveFilterConflict(true)}>
                            采用云端条件
                          </button>
                          <button type="button" onClick={() => resolveFilterConflict(false)}>
                            保存当前条件
                          </button>
                        </>
                      ) : !filterSaveStatus?.offline &&
                        !filterSaveStatus?.storageError &&
                        !filterSaveStatus?.deleted ? (
                        <button
                          type="button"
                          onClick={() => {
                            if (filterSaveStatus?.error) void filterPreferences.retry(filterScope);
                            else void filterPreferences.refresh();
                          }}
                        >
                          重试
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                  {!filterScope && filtersOpen ? (
                    <div className="collection-filters">
                      <Select
                        aria-label="内容来源"
                        value={legacyConditions.platform || ''}
                        onChange={(event) =>
                          updateSearch({
                            platform: event.target.value || undefined,
                            item: undefined,
                          })
                        }
                      >
                        <option value="">全部来源</option>
                        {[
                          'web',
                          'wechat',
                          'zhihu',
                          'xiaohongshu',
                          'weibo',
                          'x',
                          'bilibili',
                          'youtube',
                        ].map((platform) => (
                          <option key={platform} value={platform}>
                            {platformName(platform)}
                          </option>
                        ))}
                      </Select>
                      <Select
                        aria-label="保存状态"
                        value={legacyConditions.status || ''}
                        onChange={(event) =>
                          updateSearch({
                            status: event.target.value || undefined,
                            item: undefined,
                          })
                        }
                      >
                        <option value="">全部状态</option>
                        <option value="completed">已完成</option>
                        <option value="queued">等待获取</option>
                        <option value="running">正在获取</option>
                        <option value="failed">获取未完成</option>
                      </Select>
                      <Select
                        aria-label="内容类型"
                        value={legacyConditions.kind || ''}
                        onChange={(event) =>
                          updateSearch({ kind: event.target.value || undefined, item: undefined })
                        }
                      >
                        <option value="">全部类型</option>
                        <option value="article">文章</option>
                        <option value="post">笔记</option>
                        <option value="video">视频</option>
                      </Select>
                      <Select
                        aria-label="保存完整度"
                        value={legacyConditions.quality || ''}
                        onChange={(event) =>
                          updateSearch({
                            quality: event.target.value || undefined,
                            item: undefined,
                          })
                        }
                      >
                        <option value="">全部完整度</option>
                        <option value="complete">完整保存</option>
                        <option value="partial">部分保存</option>
                        <option value="link_only">仅保存链接</option>
                      </Select>
                      {search.platform || search.status || search.kind || search.quality ? (
                        <IconButton label="清除筛选" onClick={clearFilters}>
                          <X size={14} />
                        </IconButton>
                      ) : null}
                    </div>
                  ) : null}
                  {selecting ? (
                    <div className="batch-toolbar">
                      <label className="checkbox-row">
                        <Input
                          type="checkbox"
                          checked={
                            items.length > 0 && selected.size === Math.min(items.length, 100)
                          }
                          onChange={(event) =>
                            setSelected(
                              event.target.checked
                                ? new Set(items.slice(0, 100).map((item) => item.id))
                                : new Set(),
                            )
                          }
                        />
                        {selected.size
                          ? `${selected.size} 条`
                          : items.length > 100
                            ? '选择前 100 条'
                            : '选择已加载内容'}
                      </label>
                      <div className="row">
                        <IconButton
                          label="整理所选内容"
                          disabled={!selected.size || batch.isPending}
                          onClick={() => setOrganize(items.filter((item) => selected.has(item.id)))}
                        >
                          <FolderSimple size={17} />
                        </IconButton>
                        <IconButton
                          label="收藏所选内容"
                          disabled={!selected.size || batch.isPending}
                          onClick={() => batch.mutate({ favorite: true })}
                        >
                          <Star size={17} />
                        </IconButton>
                        <IconButton
                          label="取消收藏所选内容"
                          disabled={!selected.size || batch.isPending}
                          onClick={() => batch.mutate({ favorite: false })}
                        >
                          <Star size={17} weight="duotone" />
                        </IconButton>
                        <Dropdown label="更多批量操作" icon={<DotsThree size={17} />}>
                          {search.view === 'trash' ? (
                            <MenuAction
                              danger
                              disabled={!selected.size}
                              onClick={() => {
                                setBulkAction('delete');
                                setBulkError('');
                              }}
                            >
                              <Trash size={16} />
                              永久删除所选内容
                            </MenuAction>
                          ) : (
                            <MenuAction
                              disabled={!selected.size}
                              onClick={() => {
                                setBulkAction('refresh');
                                setBulkError('');
                              }}
                            >
                              <ArrowClockwise size={16} />
                              重新获取所选内容
                            </MenuAction>
                          )}
                        </Dropdown>
                        <IconButton
                          label={search.view === 'trash' ? '恢复所选内容' : '移入回收站'}
                          disabled={!selected.size || batch.isPending}
                          onClick={() => batch.mutate({ trashed: search.view !== 'trash' })}
                        >
                          {search.view === 'trash' ? (
                            <ArrowClockwise size={17} />
                          ) : (
                            <Trash size={17} />
                          )}
                        </IconButton>
                        <IconButton
                          label="取消选择"
                          onClick={() => {
                            setSelecting(false);
                            setSelected(new Set());
                          }}
                        >
                          <X size={17} />
                        </IconButton>
                      </div>
                    </div>
                  ) : null}
                  <div ref={collectionScroll} className="collection-scroll" id="main-content">
                    {search.view === 'trash' ? (
                      <p className="trash-hint">
                        回收站中的内容保留 {usageQuery.data?.limits.trashDays || 30}{' '}
                        天，可随时恢复。
                      </p>
                    ) : null}
                    {query.isError ? (
                      <div className="inline-error">
                        <ErrorNotice
                          error={query.error}
                          retry={() => {
                            void query.refetch();
                          }}
                        />
                      </div>
                    ) : null}
                    {restoringFilters || query.isPending ? (
                      <CollectionSkeleton mode={mode} />
                    ) : items.length ? (
                      <>
                        <CollectionLayout
                          mode={mode}
                          count={items.length}
                          searching={Boolean(search.q)}
                        >
                          {items.map((item) =>
                            mode === 'list' ? (
                              <ItemRow
                                key={item.id}
                                item={item}
                                actions={actions}
                                selected={search.item === item.id}
                                checked={selected.has(item.id)}
                                selecting={selecting}
                                onCheck={check}
                              />
                            ) : (
                              <ItemCard
                                key={item.id}
                                item={item}
                                actions={actions}
                                selected={search.item === item.id}
                                checked={selected.has(item.id)}
                                selecting={selecting}
                                onCheck={check}
                                masonry={mode === 'masonry'}
                              />
                            ),
                          )}
                        </CollectionLayout>
                        <div className="pagination">
                          {query.hasNextPage ? (
                            <Button
                              busy={query.isFetchingNextPage}
                              onClick={() => {
                                void query.fetchNextPage();
                              }}
                            >
                              加载更多
                              <CaretDown size={14} />
                            </Button>
                          ) : (
                            <span>已显示全部内容</span>
                          )}
                        </div>
                      </>
                    ) : !query.isError ? (
                      <EmptyState
                        icon={
                          search.view === 'favorites' ? (
                            <Star size={23} />
                          ) : search.view === 'trash' ? (
                            <Trash size={23} />
                          ) : search.q ? (
                            <MagnifyingGlass size={23} />
                          ) : (
                            <BookOpen size={23} />
                          )
                        }
                        title={
                          search.q ||
                          search.status ||
                          search.platform ||
                          search.kind ||
                          search.quality
                            ? '没有符合筛选条件的内容'
                            : search.view === 'favorites'
                              ? '把重要的内容放近一点'
                              : search.view === 'trash'
                                ? '回收站是空的'
                                : '尚未保存内容'
                        }
                        description={
                          search.q ||
                          search.status ||
                          search.platform ||
                          search.kind ||
                          search.quality
                            ? '试试其他关键词，或减少筛选条件。'
                            : search.view === 'favorites'
                              ? '点亮内容旁的星星，值得反复阅读的内容会出现在这里。'
                              : search.view === 'trash'
                                ? '删除的内容会先来到这里。'
                                : '粘贴链接，正文与图片会自动保存。'
                        }
                        action={
                          search.q ||
                          search.status ||
                          search.platform ||
                          search.kind ||
                          search.quality ? (
                            <div className="search-empty-actions">
                              {activeFilters.length ? (
                                <Button onClick={clearFilters}>清除筛选</Button>
                              ) : null}
                              {scopedSearch ? (
                                <Button onClick={searchAll}>搜索全部内容</Button>
                              ) : null}
                              {!activeFilters.length && !scopedSearch ? (
                                <span className="field-hint">
                                  试试更短的关键词，搜索词仍保留在上方。
                                </span>
                              ) : null}
                            </div>
                          ) : search.view !== 'trash' && search.view !== 'favorites' ? (
                            <Button variant="primary" onClick={() => setSaveOpen(true)}>
                              <Plus size={16} />
                              保存第一个链接
                            </Button>
                          ) : undefined
                        }
                      />
                    ) : null}
                  </div>
                </section>
              )}
            </CollectionFilters>
            {desktop && split ? (
              <DesktopResizeHandle
                kind="list"
                value={Math.round(boundedWidth)}
                maximum={Math.max(300, stageWidth - 381)}
                onChange={setListWidth}
              />
            ) : split ? (
              <div
                className="pane-resizer"
                role="separator"
                aria-label="调整内容列表宽度"
                aria-orientation="vertical"
                aria-valuemin={300}
                aria-valuemax={Math.max(300, stageWidth - 381)}
                aria-valuenow={Math.round(boundedWidth)}
                tabIndex={0}
                onDoubleClick={() => {
                  setListWidth(480);
                  try {
                    localStorage.setItem('kiipu.list-width.v1', '480');
                  } catch {}
                }}
                onKeyDown={(event) => {
                  if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
                    event.preventDefault();
                    const width = boundedWidth + (event.key === 'ArrowLeft' ? -20 : 20);
                    setListWidth(width);
                    try {
                      localStorage.setItem('kiipu.list-width.v1', String(width));
                    } catch {}
                  }
                }}
                onPointerDown={(event) => {
                  event.preventDefault();
                  const start = event.clientX;
                  const original = boundedWidth;
                  document.body.style.cursor = 'col-resize';
                  document.body.style.userSelect = 'none';
                  let last = original;
                  const move = (event: PointerEvent) => {
                    last = Math.max(
                      300,
                      Math.min(stageWidth - 381, original + event.clientX - start),
                    );
                    setListWidth(last);
                  };
                  const end = () => {
                    window.removeEventListener('pointermove', move);
                    window.removeEventListener('pointerup', end);
                    window.removeEventListener('pointercancel', end);
                    document.body.style.cursor = '';
                    document.body.style.userSelect = '';
                    try {
                      localStorage.setItem('kiipu.list-width.v1', String(last));
                    } catch {}
                  };
                  window.addEventListener('pointermove', move);
                  window.addEventListener('pointerup', end);
                  window.addEventListener('pointercancel', end);
                  resizerCleanup.current = end;
                }}
              />
            ) : null}
            {search.item ? (
              <Reader
                key={search.item}
                id={search.item}
                onClose={() => {
                  updateSearch({ item: undefined });
                  setFocus(false);
                }}
                actions={actions}
                next={next}
                previous={previous}
                focus={focus}
                onToggleFocus={() => setFocus((value) => !value)}
              />
            ) : (
              <section className="reader-pane reader-empty">
                <div className="reader-toolbar">
                  <span className="subtle">你的阅读空间</span>
                </div>
                <EmptyState
                  icon={<BookOpen size={23} />}
                  title="好内容，值得慢慢读"
                  description="从左侧选择一条内容，在这里继续阅读。"
                />
                <div className="reader-shortcuts">
                  <span>
                    <kbd>J</kbd>
                    <kbd>K</kbd>切换内容
                  </span>
                  <span>
                    <kbd>N</kbd>保存链接
                  </span>
                </div>
              </section>
            )}
          </>
        )}
      </main>
      {/* Paint native no-drag controls after the titlebar drag surfaces. */}
      {desktop ? <DesktopNavigation expanded={sidebarExpanded} toggle={toggleSidebar} /> : null}
      {desktop ? (
        <Suspense fallback={null}>
          <DesktopCommandPalette
            folders={folders}
            tags={tags}
            onSave={() => setSaveOpen(true)}
            onRefresh={refresh}
            onToggleSidebar={toggleSidebar}
            onSearchCurrent={showCollectionSearch}
          />
        </Suspense>
      ) : null}
      <SaveDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        folders={folders}
        tags={tags}
        folderId={search.folderId}
        onSaved={(id) => {
          if (search.view === 'trash') go({ item: id });
          else updateSearch({ item: id });
        }}
      />
      <GroupManager
        key={manager || 'closed'}
        kind={manager}
        onClose={() => setManager(null)}
        folders={folders}
        tags={tags}
        onDeleted={(ids) => {
          if (search.folderId && ids.includes(search.folderId)) go({ view: 'unfiled' });
          if (search.tagId && ids.includes(search.tagId)) go({ view: 'all' });
        }}
      />
      {organize?.length ? (
        <OrganizeDialog
          ids={organize.map((item) => item.id)}
          initialFolderId={organize.length === 1 ? organize[0]?.folderId : undefined}
          initialTagIds={organize.length === 1 ? organize[0]?.tagIds : undefined}
          folders={folders}
          tags={tags}
          onClose={() => setOrganize(null)}
          onSaved={() => {
            toast('内容已整理');
            setSelected(new Set());
            setSelecting(false);
          }}
        />
      ) : null}
      <Modal
        open={Boolean(bulkAction)}
        onOpenChange={(open) => {
          if (!open && !bulkProgress) setBulkAction(null);
        }}
        title={bulkAction === 'delete' ? '永久删除所选内容' : '重新获取所选内容'}
        description={
          bulkAction === 'delete'
            ? '正文、图片与备注将永久删除，无法恢复。'
            : '现有快照会保留。每条获取任务会占用额度，部分失败时可以单独重试。'
        }
      >
        <div className="stack">
          <p className="muted">
            {bulkProgress
              ? `正在处理 ${bulkProgress.done} / ${bulkProgress.total} 条`
              : `已选择 ${selected.size} 条内容`}
          </p>
          <ErrorNotice error={bulkError} />
          <div className="modal-actions">
            <Button disabled={Boolean(bulkProgress)} onClick={() => setBulkAction(null)}>
              取消
            </Button>
            <Button
              variant={bulkAction === 'delete' ? 'danger' : 'primary'}
              busy={Boolean(bulkProgress)}
              disabled={!selected.size}
              onClick={() => {
                void runBulkAction();
              }}
            >
              {bulkError ? '重试未完成内容' : '确认处理'}
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        open={Boolean(permanentDelete)}
        onOpenChange={(open) => {
          if (!open) setPermanentDelete(null);
        }}
        title="永久删除内容"
        description="正文、图片与备注将被删除，无法恢复。"
      >
        <div className="stack">
          <p className="muted">{permanentDelete?.title}</p>
          <ErrorNotice error={deleteMutation.error} />
          <div className="modal-actions">
            <Button onClick={() => setPermanentDelete(null)}>取消</Button>
            <Button
              variant="danger"
              busy={deleteMutation.isPending}
              onClick={() => permanentDelete && deleteMutation.mutate(permanentDelete.id)}
            >
              永久删除
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
